/**
 * @voltxampere/spice — Codec registry
 *
 * The 16 element codecs are imported here and exposed through two
 * lookup helpers: `findCodecByLetter` (used by from-spice) and
 * `findCodecBySchemaType` (used by to-spice).
 *
 * Sprint 16 ships codecs for: R, C, L, V (DC + AC + PULSE + SIN),
 * I, D (incl. LED + Zener model dispatch), Q (NPN + PNP), M (NMOS
 * + PMOS), J, S, X-opamp, plus the synthetic ground codec.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { resistorCodec } from "./resistor.js";
import { capacitorCodec } from "./capacitor.js";
import { inductorCodec } from "./inductor.js";
import { voltageSourceCodec } from "./voltage-source.js";
import { currentSourceCodec } from "./current-source.js";
import { diodeCodec } from "./diode.js";
import { ledCodec } from "./led.js";
import { bjtCodec } from "./bjt.js";
import { mosfetCodec } from "./mosfet.js";
import { jfetCodec } from "./jfet.js";
import { switchCodec } from "./switch.js";
import { potentiometerCodec } from "./potentiometer.js";
import { opampCodec } from "./opamp.js";
import { pulseSourceCodec } from "./pulse-source.js";
import { acSourceCodec } from "./ac-source.js";
import { groundCodec } from "./ground.js";
// Order matters for letter-shared codecs (V → pulse/ac/dc, D →
// led/diode). The first codec that returns ok wins, so the more
// specific syntax matchers must come before the generic ones.
export const ALL_CODECS = Object.freeze([
    resistorCodec,
    capacitorCodec,
    inductorCodec,
    pulseSourceCodec, // V with PULSE(...) — must beat voltageSource
    acSourceCodec, // V with SIN(...) / AC — must beat voltageSource
    voltageSourceCodec,
    currentSourceCodec,
    ledCodec, // D with LED model — must beat diodeCodec
    diodeCodec,
    bjtCodec,
    mosfetCodec,
    jfetCodec,
    switchCodec,
    potentiometerCodec,
    opampCodec,
    groundCodec,
]);
/**
 * Map letter → ordered list of codecs that claim it. Multiple
 * codecs may share a letter (V → voltage-source / pulse-source /
 * ac-source); the first one whose `parse()` returns `ok: true`
 * wins. Codecs decide based on syntactic markers (`PULSE(...)`,
 * `SIN(...)`, `AC ...`).
 *
 * Codec order in `ALL_CODECS` matters — pulse + ac come AFTER
 * voltage-source so their pre-check has the chance to claim the
 * line first via the dispatch helper below.
 */
export function findCodecsByLetter(letter) {
    const upper = letter.toUpperCase();
    return ALL_CODECS.filter((c) => c.spiceLetter.toUpperCase() === upper);
}
export function findCodecBySchemaType(schemaType) {
    for (const codec of ALL_CODECS) {
        if (codec.owns(schemaType))
            return codec;
    }
    return null;
}
//# sourceMappingURL=index.js.map